// Shared comment/reference helper utilities for sheet rows and command suggestion references.
import type { EntryListCommentPart } from "./types";

interface CommentRowLike {
  displayReference: string;
  letter: string;
  comment?: string;
}

export function resolveEntryListComment(
  row: CommentRowLike,
  uberCommentsByLetter: Record<string, string>,
): string {
  return resolveEntryListCommentParts(row, uberCommentsByLetter).map((part) => part.text).join(" / ");
}

export function resolveEntryListCommentParts(
  row: CommentRowLike,
  uberCommentsByLetter: Record<string, string>,
): EntryListCommentPart[] {
  const normalizedDisplayReference = row.displayReference.toLowerCase();
  const normalizedBaseReference = normalizedDisplayReference.split(".")[0];
  const baseComment = uberCommentsByLetter[normalizedBaseReference];
  const exactComment = uberCommentsByLetter[normalizedDisplayReference];
  const parts: EntryListCommentPart[] = [];

  if (normalizedDisplayReference === normalizedBaseReference) {
    if (exactComment) {
      parts.push({ text: exactComment, kind: "top-level" });
    }
  } else {
    if (baseComment) {
      parts.push({ text: baseComment, kind: "top-level" });
    }

    if (exactComment) {
      parts.push({ text: exactComment, kind: "sub-item" });
    }
  }

  if (row.comment) {
    parts.push({ text: row.comment, kind: "line-item" });
  }

  return parts;
}

export function buildCommentReferenceSuggestions(references: string[]): string[] {
  const expandedReferences = references.flatMap((reference) => {
    const normalizedReference = reference.toLowerCase();
    const topLevelReference = normalizedReference.split(".")[0];
    return topLevelReference === normalizedReference
      ? [normalizedReference]
      : [topLevelReference, normalizedReference];
  });

  return Array.from(new Set(expandedReferences)).sort((left, right) => left.localeCompare(right));
}

export function buildCommentReferenceHelpByReference(
  references: string[],
  uberCommentsByLetter: Record<string, string>,
): Record<string, string> {
  return references.reduce<Record<string, string>>((accumulator, reference) => {
    const normalizedReference = reference.toLowerCase();
    const exactComment = uberCommentsByLetter[normalizedReference];

    if (exactComment) {
      accumulator[normalizedReference] = exactComment;
    }

    return accumulator;
  }, {});
}
